/*
 * Copyright (c) 2024 Bolmate B.V.
 */
import './style/imagePreview.scss';

import React, {ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {ActivateHandle} from '../../util/interfaces';
import {CloseButton, WithBackground} from '@dvrd/dvr-controls';
import classNames from 'classnames';

interface Props {
    className?: string;
}

function ImagePreview(props: Props, ref: ForwardedRef<ActivateHandle<File | Blob | string | null>>) {
    const [image, setImage] = useState('');
    const [active, setActive] = useState(false);
    const isObjectURL = useRef(false);

    function onActivate(image: File | Blob | string | null) {
        if (image === null) return;
        let _isObjectURL = false;
        if (image instanceof File || image instanceof Blob) {
            image = URL.createObjectURL(image);
            _isObjectURL = true;
        }
        isObjectURL.current = _isObjectURL;
        setImage(image);
        setActive(true);
    }

    function onClose() {
        setActive(false);
        cleanup();
    }

    function cleanup() {
        if (isObjectURL.current && image.length) URL.revokeObjectURL(image);
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    useEffect(() => {
        return function() {
            cleanup();
        };
    }, [cleanup]);

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className={classNames('bolmate-image-preview default-popup', props.className)}>
                <div className='header-container'>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    <img src={image} alt='' className='image'/>
                </div>
            </div>
        </WithBackground>
    );
}

ImagePreview.displayName = 'ImagePreview';

export default forwardRef<ActivateHandle, Props>(ImagePreview);